const getReceipt = async function ({ commit, rootState }, orderId) {
    try {
        const { data } = await this.$axios.get(
            `${this.$config.platform.apis.receipt}${orderId}?permId=${rootState.permId}`
        )

        commit('setReceiptData', data)
    } catch (err) {
        throw err.response.data
    }
}

const getCheckout = async function ({ rootState, commit }) {
    const resp = await this.$axios.get(
        `${this.$config.platform.apis.checkout}${rootState.permId}`,
        {
            headers: {
                'x-site': this.$sitewideData.sitePrefix,
            },
        }
    )

    commit('setCheckoutData', resp.data)
}

const addGiftCard = async function ({ rootState, commit }, token) {
    try {
        const resp = await this.$axios.post(
            `${this.$config.platform.apis.checkout}${rootState.permId}/giftcards`,
            {
                number: token,
            },
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            }
        )
        commit('setCheckoutData', resp.data)
    } catch (err) {
        throw err.response.data
    }
}

const removeGiftCard = async function ({ rootState, commit }, token) {
    try {
        const resp = await this.$axios.delete(
            `${this.$config.platform.apis.checkout}${rootState.permId}/giftcards/${token}`,
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            }
        )

        commit('setCheckoutData', resp.data)
    } catch (err) {
        throw err.response.data
    }
}

const addPromoCode = function ({ rootState, dispatch }, promoCodeId) {
    const reqBody = {
        promoCodeId,
    }

    this.$uiEvents.$emit('promoCodeEntered', promoCodeId)

    return new Promise((resolve, reject) => {
        this.$axios
            .post(`${this.$config.platform.apis.checkout}${rootState.permId}/coupons`, reqBody, {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            })
            .then(async (resp) => {
                // note: it's possible that this later becomes 'applied' if they added this code first, then added the item to the cart, but there isn't a clean way to figure that out
                // as the cart items just have a promo.text prop that is just filled with text.  It would be nice if the cart response included a flag when an item first gets a promo
                // applied, and what it's code was etc..
                if (resp.data && resp.data.code === 'SUCCESS')
                    this.$uiEvents.$emit('promoCodeApplied', promoCodeId)

                const checkoutDispatch = dispatch('getCheckout')
                const cartDispatch = dispatch('cart/getCart', null, { root: true })
                await Promise.all([checkoutDispatch, cartDispatch])
                resolve(resp.data)
            })
            .catch((err) => {
                this.$uiEvents.$emit('promoCodeDenied', promoCodeId)
                reject(err.response.data)
            })
    })
}

const removePromoCode = function ({ rootState, dispatch }, promoCode) {
    return new Promise((resolve, reject) => {
        this.$axios
            .delete(
                `${this.$config.platform.apis.checkout}${rootState.permId}/coupons/${promoCode}`,
                {
                    headers: {
                        'x-site': this.$sitewideData.sitePrefix,
                    },
                }
            )
            .then(async (resp) => {
                const checkoutDispatch = dispatch('getCheckout')
                const cartDispatch = dispatch('cart/getCart', null, { root: true })
                await Promise.all([checkoutDispatch, cartDispatch])
                this.$uiEvents.$emit('promoCodeRemoved', promoCode)
                resolve(resp.data)
            })
            .catch((err) => reject(err.response.data))
    })
}

const subscribe = function ({ commit }) {
    commit('subscribe')
}

const setShipping = async function ({ dispatch, rootState, commit }, data) {
    try {
        await this.$axios.post(
            `${this.$config.platform.apis.checkout}${rootState.permId}/shipping`,
            data,
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            }
        )
    } catch (err) {
        throw handleCheckoutError(err, dispatch)
    }
}

const setBilling = async function ({ dispatch, rootState, commit }, data) {
    try {
        await this.$axios.post(
            `${this.$config.platform.apis.checkout}${rootState.permId}/billing`,
            data,
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            }
        )
    } catch (err) {
        throw handleCheckoutError(err, dispatch)
    }
}

// Lets try to ditch shouldSave in the url, that's dumb
const placeStripeOrder = async function ({ commit, dispatch, rootState }, stripeResponse) {
    try {
        const { data } = await this.$axios.post(
            `${this.$config.platform.apis.checkout}${rootState.permId}/Stripe`,
            { raw: stripeResponse, token: stripeResponse.token.id },
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                    ...(this.$forter.enabled && { 'x-forter-token': this.$forter.getToken() }),
                },
            }
        )

        commit('setHasOrderBeenPlaced', true)
        await dispatch('getReceipt', data.orderId)

        return data.orderId
    } catch (err) {
        throw handleCheckoutError(err, dispatch)
    }
}

// Braintree tokens expire after 24 hours. So we need to fetch them new each time.
const getBraintreeClientID = async function ({ dispatch }) {
    try {
        const resp = await this.$axios.get(
            `${this.$config.platform.apis.checkout}braintreeclient`,
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            }
        )

        return resp.data.clientToken
    } catch (err) {
        throw parseErrors(err, dispatch)
    }
}

const placePaypalOrder = async function ({ commit, dispatch, rootState }, { token }) {
    try {
        const { data } = await this.$axios.post(
            `${this.$config.platform.apis.checkout}${rootState.permId}/Paypal`,
            {
                token,
            },
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                    ...(this.$forter.enabled && { 'x-forter-token': this.$forter.getToken() }),
                },
            }
        )
        commit('setHasOrderBeenPlaced', true)

        await dispatch('getReceipt', data.orderId)
        return data.orderId
    } catch (err) {
        throw handleCheckoutError(err, dispatch)
    }
}

const placeGiftCardOrder = async function ({ commit, dispatch, rootState }) {
    try {
        const { data } = await this.$axios.post(
            `${this.$config.platform.apis.checkout}${rootState.permId}/GiftCard`,
            {},
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                    ...(this.$forter.enabled && { 'x-forter-token': this.$forter.getToken() }),
                },
            }
        )
        commit('setHasOrderBeenPlaced', true)

        await dispatch('getReceipt', data.orderId)

        return data.orderId
    } catch (err) {
        throw handleCheckoutError(err, dispatch)
    }
}

const placeAffirmOrder = async function ({ commit, dispatch, rootState }, token) {
    try {
        const { data } = await this.$axios.post(
            `${this.$config.platform.apis.checkout}${rootState.permId}/Affirm`,
            { token },
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                    ...(this.$forter.enabled && { 'x-forter-token': this.$forter.getToken() }),
                },
            }
        )
        commit('setHasOrderBeenPlaced', true)

        await dispatch('getReceipt', data.orderId)

        return data.orderId
    } catch (err) {
        throw handleCheckoutError(err, dispatch)
    }
}

const getAffirmObject = async function (
    { rootState, dispatch },
    { userConfirmationUrl, userCancelUrl }
) {
    const baseUrl = this.app.context.isDev
        ? 'https://localhost:8080'
        : `https://${this.$sitewideData.domain}`

    try {
        const resp = await this.$axios.post(
            `${this.$config.platform.apis.checkout}${rootState.permId}/AffirmObject`,
            {
                userConfirmationUrl: `${baseUrl}${userConfirmationUrl}`,
                userCancelUrl: `${baseUrl}${userCancelUrl}`,
            },
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            }
        )

        return resp.data
    } catch (err) {
        throw parseErrors(err, dispatch)
    }
}

// a little ui helper
const setIsProcessing = function ({ commit }, status) {
    commit('setIsProcessing', status)
}

const setIsAffirmPayFlow = function ({ commit }, bool) {
    commit('setIsAffirmPayFlow', bool)
}

export default {
    getCheckout,
    addGiftCard,
    removeGiftCard,
    addPromoCode,
    removePromoCode,
    subscribe,
    setShipping,
    setBilling,
    getBraintreeClientID,
    placeStripeOrder,
    placePaypalOrder,
    placeGiftCardOrder,
    placeAffirmOrder,
    getAffirmObject,
    setIsProcessing,
    setIsAffirmPayFlow,
    getReceipt,
}

function handleCheckoutError(err, dispatch) {
    // Pull message out of error response payload
    const message = err.response?.data?.msg || 'There was a problem processing your payment.'

    // Clear errors and add the new error
    dispatch('clearGeneralErrors', {}, { root: true }).then(() => {
        dispatch('addGeneralError', { message }, { root: true })
    })

    return err
}

function parseErrors(err, dispatch) {
    // something bad happened, is it a soft error?
    if (err.response && err.response.data && err.response.data.errors)
        return err.response.data.errors

    if (err.response && err.response.data && err.response.data.code === 'SERVER_ERROR') {
        // err.response.data.code == 'SERVER_ERROR' (and msg) -- paypal payment error, bad format :(
        if (err.response.data.msg) err = { message: err.response.data.msg }
        else if (err.response.data.title) err = { message: err.response.data.title } // used In Real Life??
    }

    // shrug
    dispatch('addGeneralError', err, { root: true })
}
